import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  useTheme,
  Paper,
  Divider,
  Avatar,
  Stack,
  keyframes,
} from '@mui/material';
import { Send, AutoAwesome, Person } from '@mui/icons-material';
import { openai } from '../../services/openai';
import { motion } from 'framer-motion';
import LoadingMessage from '../LoadingMessage';

const POST_ANALYSIS_QUESTIONS = [
  "Can you explain the correct answer choice in more detail?",
  "Why is this answer choice better than the others?",  
  "Can you explain the correct answer choice in simpler terms?",
];

const flowAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const shimmerAnimation = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const Message = ({ message, isUser }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, sm: 2 },
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        bgcolor: isUser ? 'transparent' : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderBottom: 1,
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      }}
    >
      <Avatar
        sx={{
          bgcolor: isUser ? 
            (theme.palette.mode === 'dark' ? '#404040' : '#e0e0e0') : 
            theme.palette.primary.main,
          width: { xs: 28, sm: 32 },
          height: { xs: 28, sm: 32 },
        }}
      >
        {isUser ? <Person sx={{ fontSize: { xs: 16, sm: 20 } }} /> : <AutoAwesome sx={{ fontSize: { xs: 16, sm: 20 } }} />}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {isUser ? 'You' : 'AI Tutor'}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            whiteSpace: 'pre-wrap', 
            lineHeight: 1.6,
            color: theme.palette.mode === 'dark' ? '#E4E4E4' : '#141414',
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          }}
        >
          {message.content}
        </Typography>
      </Box>
    </Box>
  );
};

const AIChatbot = ({ questionContext, messages: initialMessages, onChatUpdate }) => {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(initialMessages || []);
  const [loading, setLoading] = useState(false);
  const [isQuestionAnalyzed, setIsQuestionAnalyzed] = useState(false);

  useEffect(() => {
    setMessages(initialMessages || []);
  }, [initialMessages]);

  useEffect(() => {
    setIsQuestionAnalyzed(!!questionContext?.questionType);
  }, [questionContext]);

  const handlePreAnalysisMessage = async (text) => {
    if (!text.trim()) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await openai.chat.completions.create({
        model: "o3-mini",
        messages: [
          {
            role: "user",
            content: "You are an expert LSAT tutor. Help analyze this LSAT question by breaking down its structure, identifying key elements, and explaining the logical relationships. Focus on helping the student understand the fundamental aspects of the question before diving into answer choices. Make sure to explain in concise and clear terms."
          },
          ...newMessages
        ],
          response_format: {
          type: "text"
        },
        reasoning_effort: "high"
      });

      const aiMessage = response.choices[0].message;
      const updatedMessages = [...newMessages, aiMessage];
      setMessages(updatedMessages);
      onChatUpdate(updatedMessages);
    } catch (error) {
      console.error('Error getting AI response:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostAnalysisMessage = async (text) => {
    if (!text.trim()) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content: `You are an expert LSAT tutor helping a student understand a specific question.
            Question Type: ${questionContext.questionType}
            Summary: ${questionContext.summary}
            Answer Analysis: ${questionContext.answerAnalysis}
            
            Focus on explaining the reasoning clearly and helping the student understand why certain answers are correct or incorrect. Be concise but thorough in your explanations.`
          },
          ...newMessages
        ],
        temperature: 0.5,
        max_tokens: 800
      });

      const aiMessage = response.choices[0].message;
      const updatedMessages = [...newMessages, aiMessage];
      setMessages(updatedMessages);
      onChatUpdate(updatedMessages);
    } catch (error) {
      console.error('Error getting AI response:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (text) => {
    if (isQuestionAnalyzed) {
      handlePostAnalysisMessage(text);
    } else {
      handlePreAnalysisMessage(text);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(input);
    }
  };

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderRadius: 1,
      overflow: 'hidden',
    }}>
      {/* Messages Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        pb: { xs: '120px', sm: '100px' },
        height: '600px', // Fixed height for better mobile display
        maxHeight: '70vh', // Maximum height on larger screens
      }}>
        {/* Welcome Message */}
        {messages.length === 0 && (
          !isQuestionAnalyzed ? (
            <Box sx={{ 
              p: { xs: 2, sm: 4 },
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto',
            }}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  color: 'text.primary',
                }}
              >
                Let's Analyze This Question
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  color: 'text.secondary',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  maxWidth: '500px',
                  mx: 'auto',
                }}
              >
                Please ask your question below, and I'll help you analyze its structure and logical components.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              p: { xs: 2, sm: 4 },
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto',
            }}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  color: 'text.primary',
                }}
              >
                Question Analysis Complete
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  color: 'text.secondary',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  maxWidth: '500px',
                  mx: 'auto',
                }}
              >
                Now I can help you understand the answer choices and improve your approach. Try asking:
              </Typography>
              <Stack 
                direction="row" 
                spacing={1} 
                flexWrap="wrap" 
                gap={1}
                justifyContent="center"
                sx={{ px: { xs: 2, sm: 0 } }}
              >
                {POST_ANALYSIS_QUESTIONS.map((question, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    size="small"
                    onClick={() => handleSendMessage(question)}
                    sx={{
                      borderColor: 'divider',
                      color: 'text.primary',
                      textTransform: 'none',
                      borderRadius: '8px',
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.75, sm: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {question}
                  </Button>
                ))}
              </Stack>
            </Box>
          )
        )}

        {/* Chat Messages */}
        <Box sx={{ width: '100%' }}>
          {messages.map((message, index) => (
            <Message
              key={index}
              message={message}
              isUser={message.role === 'user'}
            />
          ))}

          {/* Loading Message */}
          {loading && <LoadingMessage />}
        </Box>
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: { xs: 2, sm: 3 },
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(180deg, rgba(18,18,18,0) 0%, rgba(18,18,18,1) 20%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 20%)',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            maxWidth: '800px',
            mx: 'auto',
            position: 'relative',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              p: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
              },
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Message AI Tutor..."
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                px: { xs: 1, sm: 1.5 },
                '& textarea': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  lineHeight: 1.5,
                  padding: { xs: '8px 0', sm: '12px 0' },
                },
              }}
            />
            <IconButton
              onClick={() => handleSendMessage(input)}
              disabled={!input.trim() || loading}
              sx={{
                p: { xs: 1, sm: 1.5 },
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 1.5,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
                minWidth: { xs: 36, sm: 42 },
                height: { xs: 36, sm: 42 },
              }}
            >
              <Send sx={{ fontSize: { xs: 18, sm: 22 } }} />
            </IconButton>
          </Paper>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: -24,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'text.secondary',
              width: '100%',
              textAlign: 'center',
              fontSize: { xs: '0.625rem', sm: '0.75rem' },
              px: { xs: 2, sm: 0 },
            }}
          >
            AI Tutor can make mistakes. Consider checking important information.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AIChatbot; 